import React, { FC } from "react";
import { useTranslation } from "i18n";
import {
  CrossThinIcon as CrossIcon,
  OpenInNewTabIcon,
} from "assets";
import { IconTextLink, Pill } from "component-library";
import { dataTestIds } from "data-testids";
import {
  AddOnCodes,
  addOnMoreInfoLinkMap,
  addOnNamesMap,
  addOnDescriptionMap,
} from "component-library/src/components/Organisms/Billing/constants";
import {
  AddOnCalculatedStatus,
  buyAddOnButtonNamesMap,
  SubscriptionState,
} from "app/constants";
import { IAddOn } from "services/api/BillingApi/BillingApi";
import { getStatusPillClassNames } from "components/StatusPill/StatusPill";
import SectionBox from "components/SectionBox";
interface IAddOnSectionProps {
  addOn: IAddOn;
  onCancelAddOnClick: (addOnCode: string) => void;
  onBuyAddOnClick: (code: AddOnCodes) => void;
  "data-testid"?: string;
  subscriptionState: SubscriptionState;
  endDate?: string;
}

const AddOnSection: FC<React.PropsWithChildren<IAddOnSectionProps>> = ({
  addOn,
  onCancelAddOnClick,
  onBuyAddOnClick,
  "data-testid": dataTestId,
  subscriptionState,
  endDate,
}) => {
  const { t } = useTranslation();
  const { calculatedStatus, availableToBuy, code, cancelled } = addOn;
  const isActive = calculatedStatus === AddOnCalculatedStatus.Active;

  const showCancelButton = isActive && !cancelled;
  const isAvailable =
    !showCancelButton && !cancelled && !isActive && availableToBuy;

  const link = addOnMoreInfoLinkMap[addOn.code];

  const isCanceled =
    (subscriptionState === SubscriptionState.Canceled && isActive) ||
    (addOn.cancelled && isActive);

  const isCancelAdditionalServiceButtonVisible = () =>
    calculatedStatus === SubscriptionState.Inactive ||
    subscriptionState === SubscriptionState.Canceled ||
    addOn.cancelled
      ? false
      : !addOn.availableToBuy;

  const displayPillMessage = () => {
    if (isCanceled) {
      return t("common:label.renewing.withDate", { endDate });
    }

    if (isActive) {
      return t("common:label.active");
    }

    return t("common:label.inactive");
  };

  return (
    <SectionBox
      title={t(addOnNamesMap[code])}
      description={t(addOnDescriptionMap?.[code].accountSection) || ""}
      bgColour="bg-color-neutral-three"
      pill={
        <Pill
          size="md"
          className={getStatusPillClassNames(isActive, false, isCanceled)}
          theme={isCanceled ? "dark" : "light"}
        >
          {displayPillMessage()}
        </Pill>
      }
      primaryButton={
        link && (
          <div className="absolute right-2 flex flex-1 items-center gap-1 self-start whitespace-nowrap md:static">
            <a
              href={link}
              target="_blank"
              className="text-sm font-semibold leading-snug md:text-base"
              rel="noreferrer"
            >
              {t("common:label.moreInformation")}
            </a>
            <OpenInNewTabIcon className="text-color-brand-one" />
          </div>
        )
      }
      secondaryButton={
        isAvailable && (
          <IconTextLink
            text={t(buyAddOnButtonNamesMap[code])}
            variant="button-icon-text-secondary"
            svgIconStart={
              <CrossIcon className="rotate-45" height={20} width={20} />
            }
            data-testid={
              dataTestIds.userAccountApp["MySubscription.BuyAddOn.button"]
            }
            handleClick={() => onBuyAddOnClick(code)}
          />
        )
      }
      tertiaryButton={
        isCancelAdditionalServiceButtonVisible() && (
          <IconTextLink
            text={t("common:label.cancelAdditionalService")}
            variant="button-icon-text"
            handleClick={() => onCancelAddOnClick(addOn.code)}
            className="pl-[15px] pr-[15px] text-color-brand-one"
          />
        )
      }
      data-testid={dataTestId}
    />
  );
};

export default AddOnSection;
